import React, { useState } from 'react';
import { ArrowLeft, Mail, Phone, Plus, Trash2, Check } from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const ContactSettingsScreen = () => {
  const { navigateToScreen } = useUI();
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  
  const [emails, setEmails] = useState(user?.emails || ['seckinaktunc@gmail.com']);
  const [phones, setPhones] = useState(user?.phones || ['+90 123 456 78 90']);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [isDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const handleAddEmail = () => {
    if (newEmail && !emails.includes(newEmail)) {
      setEmails([...emails, newEmail]);
      setNewEmail('');
      setShowAddEmail(false);
    }
  };

  const handleAddPhone = () => {
    if (newPhone && !phones.includes(newPhone)) {
      setPhones([...phones, newPhone]);
      setNewPhone('');
      setShowAddPhone(false);
    }
  };

  const handleRemoveEmail = (email) => {
    if (emails.length > 1) {
      setEmails(emails.filter(e => e !== email));
    }
  };

  const handleRemovePhone = (phone) => {
    if (phones.length > 1) {
      setPhones(phones.filter(p => p !== phone));
    }
  };

  const handleSave = () => {
    updateUser({
      ...user,
      emails,
      phones
    });
    
    setShowSaveSuccess(true);
    setTimeout(() => {
      setShowSaveSuccess(false);
      navigateToScreen('profile');
    }, 1500);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 flex items-center shadow-sm`}>
        <button 
          onClick={() => navigateToScreen('profile')}
          className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
        >
          <ArrowLeft className={isDarkMode ? 'text-white' : 'text-gray-800'} size={24} />
        </button>
        <h1 className={`ml-4 text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('security.emailAndContact', 'E-posta ve İletişim')}
        </h1>
      </div>

      <div className="p-4 space-y-6">
        {/* E-posta Bölümü */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('profile.emails', 'E-posta Adresleri')}
            </h2>
            <button
              onClick={() => setShowAddEmail(true)}
              className={`p-2 rounded-lg ${isDarkMode 
                ? 'bg-indigo-600 hover:bg-indigo-700' 
                : 'bg-blue-600 hover:bg-blue-700'} text-white`}
            >
              <Plus size={20} />
            </button>
          </div>

          <div className="space-y-3">
            {emails.map((email, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg 
                ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center space-x-3">
                  <Mail size={18} className={isDarkMode ? 'text-indigo-400' : 'text-blue-600'} />
                  <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{email}</span>
                </div>
                {emails.length > 1 && (
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className={`p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30`}
                  >
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {showAddEmail && (
            <div className="mt-4 flex items-center space-x-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('profile.enterEmail', 'E-posta adresini girin')}
                className={`flex-1 p-3 rounded-lg border 
                  ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'}`}
              />
              <button
                onClick={handleAddEmail}
                className={`p-3 rounded-lg ${isDarkMode 
                  ? 'bg-indigo-600 hover:bg-indigo-700' 
                  : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              >
                <Check size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Telefon Bölümü */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('profile.phones', 'Telefon Numaraları')}
            </h2>
            <button
              onClick={() => setShowAddPhone(true)}
              className={`p-2 rounded-lg ${isDarkMode 
                ? 'bg-indigo-600 hover:bg-indigo-700' 
                : 'bg-blue-600 hover:bg-blue-700'} text-white`}
            >
              <Plus size={20} />
            </button>
          </div>

          <div className="space-y-3">
            {phones.map((phone, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg 
                ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center space-x-3">
                  <Phone size={18} className={isDarkMode ? 'text-indigo-400' : 'text-blue-600'} />
                  <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{phone}</span>
                </div>
                {phones.length > 1 && (
                  <button
                    onClick={() => handleRemovePhone(phone)}
                    className={`p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30`}
                  >
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {showAddPhone && (
            <div className="mt-4 flex items-center space-x-2">
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder={t('profile.enterPhone', 'Telefon numarasını girin')}
                className={`flex-1 p-3 rounded-lg border 
                  ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'}`}
              />
              <button
                onClick={handleAddPhone}
                className={`p-3 rounded-lg ${isDarkMode 
                  ? 'bg-indigo-600 hover:bg-indigo-700' 
                  : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              >
                <Check size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Kaydet Butonu */}
        <button
          onClick={handleSave}
          className={`w-full p-4 rounded-xl font-medium flex items-center justify-center space-x-2
            ${isDarkMode 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          <span>{t('common.save', 'Kaydet')}</span>
        </button>
      </div>

      {/* Başarı Mesajı */}
      {showSaveSuccess && (
        <div className="fixed bottom-20 left-0 right-0 flex justify-center">
          <div className={`px-6 py-3 rounded-lg shadow-lg
            ${isDarkMode ? 'bg-green-900 text-green-100' : 'bg-green-100 text-green-800'}`}>
            {t('profile.saveSuccess', 'Değişiklikler kaydedildi!')}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSettingsScreen;
